import { useCallback, useMemo, useRef } from 'react';
import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import { createUrl } from '@/lib/helpers/hypertrackerHelpers';
import useServerSentEvents from '@/lib/sse/useServerSentEvents';
import useCountdownLocalized from '@/hooks/useCountdownLocalized';
import { UpdateJsonStatus } from '@/state/hypertracker/hypertrackerTypes';

const EXPIRED_REFETCH_BACKOFF_MS = 60_000;

const useStatusUpdateTimer = () => {
    const statusQuery = useQueryFetcher<UpdateJsonStatus>({
        mode: 'json',
        buildJsonUrl: () => createUrl('/status.json'),
        refetchOnWindowFocus: true,
        staleTimeMs: 30_000,
    });

    const { nextUpdate, lastUpdate } = useMemo(() => {
        return {
            nextUpdate: statusQuery.data?.nextUpdateThreshold,
            lastUpdate: statusQuery.data?.lastUpdate,
        };
    }, [statusQuery.data]);

    const lastExpiredRefetchAt = useRef<number>(0);

    const onCountdownExpired = useCallback(() => {
        const now = Date.now();
        const nextUpdateTime = nextUpdate ? new Date(nextUpdate).getTime() : 0;
        const isNextUpdateInPast = nextUpdateTime < now;

        if (isNextUpdateInPast) {
            if (now - lastExpiredRefetchAt.current < EXPIRED_REFETCH_BACKOFF_MS) {
                return;
            }
            lastExpiredRefetchAt.current = now;
        } else {
            lastExpiredRefetchAt.current = 0;
        }

        statusQuery.refetch();
    }, [nextUpdate, statusQuery.refetch]);

    useServerSentEvents(
        {},
        {
            fileInvalidation: {
                filenames: ['/status.json'],
            },
        },
    );

    const { past, future } = useCountdownLocalized({
        date1: lastUpdate || '',
        date2: nextUpdate || null,
        callback: onCountdownExpired,
        options: {
            long: false,
            units: ['minutes'],
            utc: true,
        },
    });

    return { past, future };
};

export default useStatusUpdateTimer;
